/**
 * Merge a cascade of theme documents into one resolved theme.
 *
 * CALLING CONVENTION: the ORG document goes in the first slot, always - pass it
 * as null when it has not loaded rather than omitting it - because that slot
 * owns the `enabled` kill switch (see below).
 *
 * Order is org default -> user overrides -> live draft, and
 * merging is PER TOKEN: a user who changes one color keeps every other value the
 * org set. Anything the contract does not recognise, or that could break out of
 * a CSS declaration, is dropped and reported rather than silently passed to the
 * compiler.
 */
import { isValidToken } from './tokenRegistry';
import type {
  RejectedToken, ResolveResult, ResolvedTheme, ThemeDoc, TokenMap, FontWeightKey,
} from './types';

/** Root font size the app is known to survive (the font bar's proven range). */
export const MIN_FONT_PX = 13;
export const MAX_FONT_PX = 50;
/** Density bounds. Below ~0.15rem controls collide; above ~0.5rem layouts burst. */
export const MIN_SPACE_UNIT_REM = 0.15;
export const MAX_SPACE_UNIT_REM = 0.5;

const FONT_WEIGHT_KEYS: readonly FontWeightKey[] = ['normal', 'medium', 'semibold', 'bold'];
const MIN_WEIGHT = 100;
const MAX_WEIGHT = 900;

/**
 * A token value is injected as raw CSS text, so it must not be able to close the
 * declaration or the rule, pull in a remote resource, or escape the style
 * element. Everything legitimate - triplets, lengths, rgb()/hsl() and full
 * shadow strings - lives comfortably inside this character set.
 */
const SAFE_VALUE = /^[a-zA-Z0-9\s.,%#/()_-]+$/;
const FORBIDDEN = /url\s*\(|expression\s*\(|[;{}<>\\@"']|javascript:|\/\*|\*\//i;

export function isSafeValue(value: string): boolean {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v || v.length > 300) return false;
  if (FORBIDDEN.test(v)) return false;
  return SAFE_VALUE.test(v);
}

/**
 * Font stacks legitimately contain quotes and commas, so they get their own
 * rule: still no braces, semicolons or url().
 */
const SAFE_FONT_STACK = /^[a-zA-Z0-9\s,'"._-]+$/;

export function isSafeFontStack(value: string): boolean {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v || v.length > 300) return false;
  if (FORBIDDEN.test(v.replace(/["']/g, ''))) return false;
  return SAFE_FONT_STACK.test(v);
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** Validate one mode's token map, collecting rejects. */
function mergeTokens(
  target: TokenMap,
  incoming: TokenMap | undefined,
  scope: 'tokens' | 'components',
  mode: 'light' | 'dark',
  rejected: RejectedToken[],
): void {
  if (!incoming) return;
  for (const [name, value] of Object.entries(incoming)) {
    if (!isValidToken(name)) {
      rejected.push({ scope, mode, name, value: String(value), reason: 'unknown-token' });
      continue;
    }
    if (!isSafeValue(value)) {
      rejected.push({ scope, mode, name, value: String(value), reason: 'unsafe-value' });
      continue;
    }
    target[name] = value.trim();
  }
}

export function resolveTheme(...docs: (ThemeDoc | null | undefined)[]): ResolveResult {
  const rejected: RejectedToken[] = [];
  const theme: ResolvedTheme = {
    enabled: false,
    tokens: { light: {}, dark: {} },
    components: { light: {}, dark: {} },
    typography: {},
    layout: {},
  };

  // `enabled` is an ORG-level switch and it FAILS CLOSED: only the document in
  // the first slot - the org default, by the calling convention below - may set
  // it. If that slot is null because the API failed or the org has never
  // published, theming stays off no matter what a user document says.
  //
  // The alternative (letting the first document that happens to exist own it)
  // reads as more forgiving but makes the switch fail OPEN: an org-API blip
  // would let a user's saved theme apply against an org that had disabled
  // theming. A kill switch has to mean what it says on its worst day.
  const orgDoc = docs[0];
  if (orgDoc && typeof orgDoc.enabled === 'boolean') theme.enabled = orgDoc.enabled;

  docs.forEach((doc) => {
    if (!doc) return;
    if (typeof doc.name === 'string') theme.name = doc.name;

    mergeTokens(theme.tokens.light, doc.tokens?.light, 'tokens', 'light', rejected);
    mergeTokens(theme.tokens.dark, doc.tokens?.dark, 'tokens', 'dark', rejected);
    mergeTokens(theme.components.light, doc.components?.light, 'components', 'light', rejected);
    mergeTokens(theme.components.dark, doc.components?.dark, 'components', 'dark', rejected);

    const type = doc.typography;
    if (type) {
      if (type.fontFamily !== undefined) {
        if (isSafeFontStack(type.fontFamily)) {
          theme.typography.fontFamily = type.fontFamily.trim();
        } else {
          rejected.push({
            scope: 'typography', name: 'fontFamily',
            value: String(type.fontFamily), reason: 'unsafe-value',
          });
        }
      }
      if (type.fontSizePx !== undefined) {
        if (Number.isFinite(type.fontSizePx)) {
          theme.typography.fontSizePx = clamp(type.fontSizePx, MIN_FONT_PX, MAX_FONT_PX);
        } else {
          rejected.push({
            scope: 'typography', name: 'fontSizePx',
            value: String(type.fontSizePx), reason: 'out-of-range',
          });
        }
      }
      if (type.weights) {
        const weights = { ...(theme.typography.weights ?? {}) };
        for (const [key, value] of Object.entries(type.weights)) {
          if (!FONT_WEIGHT_KEYS.includes(key as FontWeightKey)) {
            rejected.push({
              scope: 'typography', name: key, value: String(value), reason: 'unknown-token',
            });
            continue;
          }
          if (!Number.isFinite(value) || value < MIN_WEIGHT || value > MAX_WEIGHT) {
            rejected.push({
              scope: 'typography', name: key, value: String(value), reason: 'out-of-range',
            });
            continue;
          }
          weights[key as FontWeightKey] = value as number;
        }
        if (Object.keys(weights).length) theme.typography.weights = weights;
      }
    }

    const layout = doc.layout;
    if (layout) {
      if (layout.spaceUnitRem !== undefined) {
        if (Number.isFinite(layout.spaceUnitRem)) {
          theme.layout.spaceUnitRem = clamp(
            layout.spaceUnitRem, MIN_SPACE_UNIT_REM, MAX_SPACE_UNIT_REM,
          );
        } else {
          rejected.push({
            scope: 'layout', name: 'spaceUnitRem',
            value: String(layout.spaceUnitRem), reason: 'out-of-range',
          });
        }
      }
      if (layout.radius) {
        const radius = { ...(theme.layout.radius ?? {}) };
        for (const [key, value] of Object.entries(layout.radius)) {
          if (!isSafeValue(value)) {
            rejected.push({ scope: 'layout', name: `radius.${key}`, value, reason: 'unsafe-value' });
            continue;
          }
          radius[key] = value.trim();
        }
        if (Object.keys(radius).length) theme.layout.radius = radius;
      }
    }
  });

  return { theme, rejected };
}
